import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { gtmNewsletterSubmit } from '../utils/gtm';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  TextField,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { EmailOutlined } from '@mui/icons-material';
import mortgageData from '../components/Mortgages/Mortgages.json';
import { MORTGAGE_RATES } from '../data/mortgageRates';
import SEOHead from '../components/SEO/SEOHead';
import TrustStrip from '../components/Layout/TrustStrip';
import FAQSection from '../components/SEO/FAQSection';
import RateSnapshot from '../components/Mortgages/RateSnapshot';

/* ── Mortgage modules ── */
import { MortgageProvider } from '../components/Mortgages/mortgageContext';
import { getMortgageFAQs } from '../components/Mortgages/mortgageConstants';

/* ── Lazy-loaded components ── */
const MortgageSmartWizard = lazy(() => import('../components/Mortgages/MortgageSmartWizard'));
const MortgageRatesSection = lazy(() => import('../components/Mortgages/MortgageRatesSection'));
const MortgageGuideTab = lazy(() => import('../components/Mortgages/MortgageGuideTab'));
const FinmapleComparator = lazy(() => import('../components/Comparator/FinmapleComparator'));

const LazyFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
    <CircularProgress sx={{ color: '#1E3A5F' }} size={28} />
  </Box>
);

const DATE_LOCALES = { es: 'es-CA', en: 'en-CA', fr: 'fr-CA' };
const formatDate = (dateStr, lang = 'es') => {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString(DATE_LOCALES[lang] || 'es-CA', { day: 'numeric', month: 'long', year: 'numeric' });
};

/* ── Compact Newsletter for mortgage page ── */
const CompactNewsletter = ({ t }) => (
  <Box
    component="section"
    sx={{ bgcolor: '#F7F9FC', borderTop: '1px solid #EEF0F3', py: { xs: 4, md: 5 } }}
  >
    <Container maxWidth="lg">
      <Box
        component="form"
        action="https://buttondown.com/api/emails/embed-subscribe/Finmaple"
        method="post"
        onSubmit={() => {
          if (window.plausible) window.plausible('Newsletter Submit', { props: { source: 'hipotecas-compact' } });
          gtmNewsletterSubmit({ source: 'hipotecas-compact' });
        }}
        sx={{
          display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center', gap: { xs: 1.5, sm: 2 },
          maxWidth: 640, mx: 'auto',
          bgcolor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '14px',
          px: { xs: 2.5, sm: 3 }, py: { xs: 2.5, sm: 2.5 },
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}
      >
        <EmailOutlined sx={{ fontSize: 22, color: '#1E3A5F', display: { xs: 'none', sm: 'block' } }} />
        <Typography sx={{ fontSize: '14px', color: '#1A1A1A', fontWeight: 500, lineHeight: 1.5, flex: { sm: '0 0 auto' }, maxWidth: { sm: 260 } }}>
          {t('mortgagePage.compactNewsletter.text')}
        </Typography>
        <TextField
          name="email"
          type="email"
          required
          size="small"
          placeholder={t('mortgagePage.compactNewsletter.placeholder', { defaultValue: 'tu@email.com' })}
          inputProps={{ inputMode: 'email', autoComplete: 'email' }}
          sx={{
            flex: 1, minWidth: { xs: 0, sm: 170 },
            '& .MuiOutlinedInput-root': {
              height: 44, borderRadius: '10px', fontSize: '16px',
              '& fieldset': { borderColor: '#E5E7EB' },
              '&:hover fieldset': { borderColor: '#1E3A5F' },
              '&.Mui-focused fieldset': { borderColor: '#1E3A5F' },
            },
          }}
        />
        <Button
          type="submit"
          variant="contained"
          sx={{
            height: 44, px: 3, fontSize: '15px', fontWeight: 700,
            borderRadius: '10px', bgcolor: '#C23A22', color: '#fff',
            textTransform: 'none', whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(194,58,34,0.2)',
            '&:hover': { bgcolor: '#A83220', boxShadow: '0 4px 12px rgba(194,58,34,0.3)' },
          }}
        >
          {t('mortgagePage.compactNewsletter.cta')}
        </Button>
      </Box>
    </Container>
  </Box>
);

/* ── StickySubNav (3 tabs) ── */
const TAB_KEYS = [
  { id: 0, key: 'mortgagePage.tabs.rates' },
  { id: 1, key: 'mortgagePage.tabs.newcomerPrograms' },
  { id: 2, key: 'mortgagePage.tabs.stepByStep' },
];

const StickySubNav = ({ activeTab, onTabChange }) => {
  const { t } = useTranslation();
  const [isSticky, setIsSticky] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!navRef.current) return;
      const rect = navRef.current.getBoundingClientRect();
      setIsSticky(rect.top <= 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box
      ref={navRef}
      component="nav"
      role="navigation"
      aria-label={t('mortgagePage.tabNav.ariaLabel')}
      sx={{
        position: 'sticky', top: 0, zIndex: 20, bgcolor: '#FFFFFF',
        borderBottom: '1px solid', borderColor: isSticky ? '#EEF0F3' : 'transparent',
        boxShadow: isSticky ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
      }}
    >
      <Container maxWidth="lg">
        <Box
          className="mobile-scroll-container"
          sx={{ display: 'flex', gap: 0.5, py: 1, overflowX: 'auto' }}
        >
          {TAB_KEYS.map((pill) => (
            <Button
              key={pill.id}
              onClick={() => {
                onTabChange(pill.id);
                const tabNames = ['tasas', 'newcomer', 'guia'];
                if (window.plausible) window.plausible('Mortgage Tab Switch', { props: { tab: tabNames[pill.id] } });
              }}
              disableElevation
              disableRipple
              aria-current={activeTab === pill.id ? 'true' : undefined}
              sx={{
                borderRadius: 0, height: { xs: '44px', md: '40px' }, px: 2.5, fontSize: '14px',
                textTransform: 'none', flexShrink: 0, transition: 'all 0.15s ease',
                ...(activeTab === pill.id
                  ? { bgcolor: 'transparent', color: '#1E3A5F', fontWeight: 700, borderBottom: '2px solid #1E3A5F', '&:hover': { bgcolor: '#F7F9FC' } }
                  : { bgcolor: 'transparent', color: '#5F6B7A', fontWeight: 500, borderBottom: '2px solid transparent', '&:hover': { bgcolor: '#F7F9FC', color: '#1E3A5F' } }),
                '&:focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: 2 },
              }}
            >
              {t(pill.key)}
            </Button>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

/* ── Cross-links section ── */
const CROSS_LINK_DEFS = [
  { labelKey: 'mortgagePage.crossLinks.firstMortgage', path: '/blog/primera-hipoteca-canada-guia' },
  { labelKey: 'mortgagePage.crossLinks.calculator', path: '/calculadora-hipoteca' },
  { labelKey: 'mortgagePage.crossLinks.fhsa', path: '/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes' },
  { labelKey: 'mortgagePage.crossLinks.costOfLiving', path: '/blog/costo-de-vida-montreal-2026' },
  { labelKey: 'mortgagePage.crossLinks.buildCredit', path: '/blog/construir-credito-canada-desde-cero' },
  { labelKey: 'mortgagePage.crossLinks.salary', path: '/herramientas/calculadora-salario-neto' },
];

const CrossLinksSection = () => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  return (
    <Box component="section" sx={{ bgcolor: '#FFFFFF', py: { xs: 3, md: 4 } }}>
      <Container maxWidth="lg">
        <Typography sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '14px', mb: 2 }}>
          {t('mortgagePage.crossLinks.title')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {CROSS_LINK_DEFS.map((link) => {
            const to = `/${lng}${link.path}`;
            return (
              <Button
                key={link.path}
                component={RouterLink}
                to={to}
                size="small"
                onClick={() => {
                  if (window.plausible) window.plausible('Cross Link Click', { props: { from: 'hipotecas', to } });
                }}
                sx={{
                  textTransform: 'none', color: '#1E3A5F', fontSize: '13px', fontWeight: 600,
                  borderRadius: '8px', border: '1px solid #EEF0F3', px: 2,
                  '&:hover': { bgcolor: '#F7F9FC', borderColor: '#1E3A5F' },
                }}
              >
                {t(link.labelKey)}
              </Button>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

/* ── Inner page component ── */
const MortgagesPageInner = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = i18n.language;

  /* Tab state — check URL hash for deep linking */
  const [activeTab, setActiveTab] = useState(() => {
    const hash = window.location.hash;
    if (hash === '#programas-newcomer' || hash === '#programas') return 1;
    if (hash === '#guia') return 2;
    return 0;
  });

  /* Handle hash changes (e.g. from redirect) */
  useEffect(() => {
    const hash = location.hash;
    if (hash === '#programas-newcomer' || hash === '#programas') setActiveTab(1);
    else if (hash === '#guia') setActiveTab(2);
  }, [location.hash]);

  /* Best rates for wizard hero */
  const quebecBest = MORTGAGE_RATES.quebec.bestRates;
  const lowestFixed = quebecBest.find((r) => r.term === '5 años fijo')?.rate;
  const lowestVariable = quebecBest.find((r) => r.term === '5 años variable')?.rate;

  const mortgageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t('mortgagePage.schema.name'),
    description: t('mortgagePage.schema.description'),
    url: 'https://www.finmaple.com/hipotecas',
    dateModified: MORTGAGE_RATES.lastUpdated,
    publisher: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
  };

  return (
    <Box sx={{ bgcolor: '#FFFFFF' }}>
      {/* ── SEOHead ── */}
      <SEOHead
        seoKey="mortgages"
        title={t('mortgagePage.seo.title')}
        description={t('mortgagePage.seo.description')}
        canonical="/hipotecas"
        routeKey="mortgages"
        schema={mortgageSchema}
        noIndex={lang !== 'es'}
      />

      {/* ── MortgageSmartWizard (hero + wizard) ── */}
      <Suspense fallback={<LazyFallback />}>
        <MortgageSmartWizard
          mortgageData={mortgageData}
          lowestFixed={lowestFixed}
          lowestVariable={lowestVariable}
        />
      </Suspense>

      {/* ── Rate Snapshot (single consolidated strip) ── */}
      <RateSnapshot />

      {/* ── StickySubNav (3 tabs) ── */}
      <StickySubNav activeTab={activeTab} onTabChange={setActiveTab} />

      {/* ── Tab Content ── */}
      {activeTab === 0 && (
        <Suspense fallback={<LazyFallback />}>
          <MortgageRatesSection />
        </Suspense>
      )}

      {activeTab === 1 && (
        <Box sx={{ py: { xs: 3, md: 4 } }}>
          <Container maxWidth="lg">
            <Typography variant="h4" component="h2" sx={{ fontWeight: 700, color: '#1A1A1A', mb: 0.5, fontSize: { xs: '20px', md: '24px' } }}>
              {t('mortgagePage.newcomerTab.title')}
            </Typography>
            <Typography sx={{ color: '#5F6B7A', fontSize: '14px', lineHeight: 1.6, mb: 3, maxWidth: '70ch' }}>
              {t('mortgagePage.newcomerTab.subtitle')}
            </Typography>
            <Suspense fallback={<LazyFallback />}>
              <FinmapleComparator category="mortgages" />
            </Suspense>
          </Container>
        </Box>
      )}

      {activeTab === 2 && (
        <Suspense fallback={<LazyFallback />}>
          <MortgageGuideTab onSwitchTab={setActiveTab} />
        </Suspense>
      )}

      {/* ── Cross-links (always visible) ── */}
      <CrossLinksSection />

      {/* ── FAQ ── */}
      <Box component="section" sx={{ bgcolor: '#F7F9FC', py: { xs: 4, md: 5 } }}>
        <Container maxWidth="lg">
          <FAQSection
            faqs={getMortgageFAQs(t)}
            title={t('mortgagePage.faq.title')}
          />
        </Container>
      </Box>

      {/* ── Disclosure strip ── */}
      <Box
        component="section"
        sx={{ borderTop: '1px solid #F3F4F6', py: { xs: 2, md: 2.5 } }}
      >
        <Container maxWidth="lg">
          <Typography sx={{ fontSize: '12px', color: '#9CA3AF', lineHeight: 1.6 }}>
            {t('mortgagePage.disclaimer', { date: formatDate(MORTGAGE_RATES.lastUpdated, lang) })}
          </Typography>
        </Container>
      </Box>

      {/* ── Compact Newsletter ── */}
      <CompactNewsletter t={t} />

      {/* ── TrustStrip ── */}
      <TrustStrip />
    </Box>
  );
};

/* ── Page wrapper with MortgageProvider ── */
const MortgagesPage = () => (
  <MortgageProvider mortgageData={mortgageData}>
    <MortgagesPageInner />
  </MortgageProvider>
);

export default MortgagesPage;
